import { useEffect } from "react";
import { BaseModal } from "@/components/ui/base-modal";
import { track } from "@/utils/analytics";
import EntitlementGateCard from "./EntitlementGateCard";

interface EntitlementGateModalProps {
	open: boolean;
	onClose: () => void;
	/**
	 * Stable id of this gate (e.g. "property", "amortization") for analytics.
	 * Independent of the translated copy, so insights survive copy changes.
	 */
	gateId: string;
	title: string;
	description: string;
	cta: string;
}

// TODO(product-design): AvmPaywallModal reuses this card's look; merge both
// into one upgrade modal once the product designer defines the final format.
export default function EntitlementGateModal({
	open,
	onClose,
	gateId,
	title,
	description,
	cta,
}: EntitlementGateModalProps) {
	// Many screens own their own open state, so "became visible" is the one
	// point common to all of them; the funnel's other steps are click handlers.
	useEffect(() => {
		if (open) {
			track("paywall_viewed", {
				gate_id: gateId,
				path: window.location.pathname,
			});
		}
	}, [open, gateId]);

	const handleClose = () => {
		track("paywall_dismissed", {
			gate_id: gateId,
			path: window.location.pathname,
		});
		onClose();
	};

	return (
		<BaseModal
			open={open}
			onClose={handleClose}
			title={title}
			description={description}
			className="max-w-none border-none bg-transparent p-0 shadow-none dark:bg-transparent"
			showCloseButton={false}
			hideHeader={true}
			onOpenAutoFocus={(e) => e.preventDefault()}
		>
			<EntitlementGateCard
				title={title}
				description={description}
				cta={cta}
				onClose={handleClose}
				// Pricing reads ?from= to attribute the checkout to this gate.
				ctaHref={`/pricing?from=${encodeURIComponent(gateId)}`}
				ctaDataAttr={`paywall-${gateId}-upgrade`}
				onCtaClick={() =>
					track("paywall_cta_clicked", {
						gate_id: gateId,
						path: window.location.pathname,
					})
				}
			/>
		</BaseModal>
	);
}
